import { ref, onMounted, onBeforeUnmount, type Ref } from 'vue';

/**
 * Composable du drawer mobile (bottom sheet) — 3 positions : peek / mid / full.
 *
 * Logique extraite de l'app monolithique d'origine, ré-encapsulée en composable.
 * Le drawer est un élément du DOM dont la position translateY est contrôlée par
 * un attribut `data-drawer-state` sur le container racine (.app-embed-root).
 *
 * Le CSS interprète cet attribut pour positionner le drawer.
 *
 * `containerRef` = la racine de l'app (porte l'attribut data-drawer-state)
 * `handleRef`    = la poignée draggable (en haut du drawer)
 * `sidebarRef`   = le drawer lui-même (pour gérer la classe `dragging`)
 *
 * Retourne :
 *   - `drawerState` : 'peek' | 'mid' | 'full' (mobile uniquement, null sinon)
 *   - `setDrawerState(name, onChange?)` : change l'état avec callback (utilisé pour map.invalidateSize)
 *   - `isMobile()` : helper réactif
 */

export type DrawerState = 'peek' | 'mid' | 'full';

export interface UseDrawerOptions {
  containerRef: Ref<HTMLElement | null>;
  handleRef: Ref<HTMLElement | null>;
  sidebarRef: Ref<HTMLElement | null>;
  /** Callback appelé après chaque changement d'état (typiquement pour map.invalidateSize) */
  onStateChange?: (state: DrawerState) => void;
}

const MOBILE_QUERY = '(max-width: 699px)';
const PEEK_HEIGHT_PX = 124;

export function useDrawer({
  containerRef,
  handleRef,
  sidebarRef,
  onStateChange,
}: UseDrawerOptions) {
  const drawerState = ref<DrawerState | null>(null);

  function isMobile(): boolean {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  /** Hauteur visible du viewport "app" (peut différer de window.innerHeight si embarqué) */
  function appVH(): number {
    const c = containerRef.value;
    if (!c) return window.innerHeight;
    return c.clientHeight || window.innerHeight;
  }

  function setDrawerState(name: DrawerState): void {
    if (!isMobile() || !containerRef.value) return;
    containerRef.value.dataset.drawerState = name;
    drawerState.value = name;

    // Écrit la variable CSS --drawer-translate sur le sidebar pour positionner
    // visuellement le drawer. Sans ça, le CSS reste sur la valeur fallback et
    // le drawer ne bouge jamais malgré le changement d'attribut data-*.
    const sb = sidebarRef.value;
    if (sb) {
      const vh = appVH();
      let translate: number;
      if (name === 'full') translate = 0;
      else if (name === 'mid') translate = vh * 0.4;
      else translate = vh - PEEK_HEIGHT_PX;
      sb.style.setProperty('--drawer-translate', `${translate}px`);
    }

    // Délai = durée de l'anim CSS du drawer (laissée à 350ms)
    setTimeout(() => onStateChange?.(name), 350);
  }

  // ---------- Drag state ----------

  let dragging = false;
  let didDrag = false;
  let startY = 0;
  let startTranslate = 0;

  function getCurrentTranslate(): number {
    const cur = drawerState.value || 'peek';
    const vh = appVH();
    if (cur === 'full') return 0;
    if (cur === 'mid') return vh * 0.4;
    return vh - PEEK_HEIGHT_PX;
  }

  function onDragStart(e: MouseEvent | TouchEvent): void {
    if (!isMobile()) return;
    dragging = true;
    didDrag = false;
    const t = 'touches' in e ? e.touches[0] : (e as MouseEvent);
    startY = t.clientY;
    startTranslate = getCurrentTranslate();
    sidebarRef.value?.classList.add('dragging');
    e.preventDefault();
  }

  function onDragMove(e: MouseEvent | TouchEvent): void {
    if (!dragging) return;
    const t = 'touches' in e ? e.touches[0] : (e as MouseEvent);
    const delta = t.clientY - startY;
    if (Math.abs(delta) > 4) didDrag = true;
    const vh = appVH();
    const newTranslate = Math.max(0, Math.min(vh - 60, startTranslate + delta));
    sidebarRef.value?.style.setProperty('--drag-translate', `${newTranslate}px`);
    e.preventDefault();
  }

  function onDragEnd(e: MouseEvent | TouchEvent): void {
    if (!dragging) return;
    dragging = false;
    sidebarRef.value?.classList.remove('dragging');
    sidebarRef.value?.style.removeProperty('--drag-translate');

    const t = 'changedTouches' in e ? e.changedTouches[0] : (e as MouseEvent);
    const finalY = t.clientY;
    const delta = finalY - startY;
    const finalTranslate = startTranslate + delta;

    // Snap vers la position la plus proche
    const vh = appVH();
    const positions: { name: DrawerState; pos: number }[] = [
      { name: 'full', pos: 0 },
      { name: 'mid', pos: vh * 0.4 },
      { name: 'peek', pos: vh - PEEK_HEIGHT_PX },
    ];
    let best = positions[0];
    let bestDist = Infinity;
    positions.forEach((p) => {
      const d = Math.abs(p.pos - finalTranslate);
      if (d < bestDist) {
        bestDist = d;
        best = p;
      }
    });
    setDrawerState(best.name);
  }

  function onHandleClick(): void {
    // Si l'utilisateur vient de drag, on ignore le click qui suit
    if (didDrag) {
      didDrag = false;
      return;
    }
    const cur = drawerState.value || 'peek';
    const next: DrawerState = cur === 'peek' ? 'mid' : cur === 'mid' ? 'full' : 'peek';
    setDrawerState(next);
  }

  // ---------- Resize handler ----------

  function handleResize(): void {
    if (!isMobile()) {
      // Sortie du mode mobile : on retire l'attribut et la variable CSS
      // pour ne pas laisser de styles parasites en mode desktop.
      if (containerRef.value) delete containerRef.value.dataset.drawerState;
      sidebarRef.value?.style.removeProperty('--drawer-translate');
      drawerState.value = null;
    } else if (!drawerState.value) {
      setDrawerState('peek');
    } else {
      // Toujours en mobile, on recalcule la position pour l'état courant
      // (par ex. après une rotation : la hauteur du viewport a changé).
      setDrawerState(drawerState.value);
    }
    onStateChange?.(drawerState.value || 'peek');
  }

  // ---------- Lifecycle ----------

  onMounted(() => {
    if (isMobile()) {
      setDrawerState('peek');
    }

    const handle = handleRef.value;
    if (handle) {
      handle.addEventListener('mousedown', onDragStart);
      handle.addEventListener('touchstart', onDragStart, { passive: false });
      handle.addEventListener('click', onHandleClick);
    }
    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('touchmove', onDragMove, { passive: false });
    document.addEventListener('mouseup', onDragEnd);
    document.addEventListener('touchend', onDragEnd);
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
  });

  onBeforeUnmount(() => {
    const handle = handleRef.value;
    if (handle) {
      handle.removeEventListener('mousedown', onDragStart);
      handle.removeEventListener('touchstart', onDragStart);
      handle.removeEventListener('click', onHandleClick);
    }
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('touchmove', onDragMove);
    document.removeEventListener('mouseup', onDragEnd);
    document.removeEventListener('touchend', onDragEnd);
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('orientationchange', handleResize);
  });

  return {
    drawerState,
    setDrawerState,
    isMobile,
  };
}
